import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Select,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const ticketTitles = [
  "الانترنت",
  "تعيين",
  "اخلاء",
  "كمبيوتر",
  "شريحه",
  "حساب",
  "صلاحيات",
  "اخري",
];

const AddTicketDialog = ({
  open,
  onClose,
  onTicketAdded,
  employees = [],
  companies = [],
  user = {},
}) => {
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    assigned_to: "غير مخصص", // القيمة الافتراضية
  });

  const [formErrors, setFormErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState([]); // <-- Add departments state

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get("https://backenderpforseg.samielmetwali.com/getDepartments.php");
        if (response.data.success) {
          setDepartments(response.data.departments);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // استرجاع بيانات المستخدم
  let localUser = {};
  try {
    const stored = localStorage.getItem("data");
    if (stored) localUser = JSON.parse(stored);
  } catch (e) {
    localUser = {};
  }

  const handleChange = (field, value) => {
    setNewTicket((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAssignChange = (employeeId) => {
    const emp = employees.find((e) => e.employee_id === employeeId);
    setNewTicket((prev) => ({
      ...prev,
      assigned_to: employeeId,
      // username: emp?.name || "",
      department: emp?.department2 || "",
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!newTicket.title.trim()) errors.title = "يرجى اختيار العنوان";
    if (!newTicket.description.trim()) errors.description = "يرجى إدخال الوصف";
    if (!newTicket.username.trim()) errors.username = "يرجى إدخال الاسم";
    if (!newTicket.company) errors.company = "يرجى اختيار الشركة";
    if (!department.trim()) errors.department = "يرجى إدخال القسم";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setAddLoading(true);
    setAddError("");

    if (!validateForm()) {
      setAddLoading(false);
      return;
    }

    const ticketData = {
      ...newTicket,
      status: "البداية",
      username: newTicket.username,
      company: newTicket.company,
      department: department,
      category: "أخرى",
      created_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      assigned_to: newTicket.assigned_to || "غير مخصص", // للتأكيد
    };

    try {
      const formData = new FormData();
      formData.append("title", newTicket.title);
      formData.append("description", newTicket.description);
      formData.append("assigned_to", newTicket.assigned_to);
      formData.append("company", newTicket.company);
      formData.append("priority", newTicket.priority || "Normal");

      formData.append("department", department);
      formData.append("category", "أخرى");
      formData.append("status", "البداية");
      formData.append(
        "created_at",
        new Date().toISOString().slice(0, 19).replace("T", " ")
      );
      formData.append("username", newTicket.username);

      if (attachment) {
        formData.append("attachment", attachment);
      }

      const response = await axios.post(
        "https://backenderpforseg.samielmetwali.com/tickets/add_ticket.php",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.success) {
        onTicketAdded({
          ...ticketData,
          id: response.data.id || Math.random().toString(36).substr(2, 9),
        });
        setNewTicket({
          title: "",
          description: "",
          priority: "Normal",
          assigned_to: "غير مخصص",
          company: "",
          username: "",
        });
        setDepartment(""); // Clear department after submission
        setAttachment(null); // Clear attachment after submission
        setSuccessDialogOpen(true);
        onClose();
        playNotification(); // Play sound here
      } else {
        setAddError(response.data.message || "فشل في إضافة التذكرة");
      }
    } catch (err) {
      setAddError("حدث خطأ أثناء الإضافة");
    }
    setAddLoading(false);
  };

  const playNotification = () => {
    const audio = new Audio("/sounds/notification_ticket.wav");
    audio.play();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            backgroundColor: "#232526",
            color: "#fff",
            minWidth: 600,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#6fd943" }}>
          إضافة تذكرة جديدة
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth color="success">
              <InputLabel id="title-label" sx={{ color: "#6fd943" }}>
                العنوان
              </InputLabel>
              <Select
                labelId="title-label"
                value={newTicket.title}
                onChange={(e) => handleChange("title", e.target.value)}
                sx={{ background: "#2d3134", color: "#fff" }}
              >
                {ticketTitles.map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.title && (
                <small style={{ color: "red" }}>{formErrors.title}</small>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="الاسم"
              variant="filled"
              value={newTicket.username}
              onChange={(e) => handleChange("username", e.target.value)}
              sx={{
                background: "#2d3134",
                input: { color: "#fff" },
                label: { color: "#6fd943" },
              }}
              error={!!formErrors.username}
              helperText={formErrors.username}
            />

            <TextField
              fullWidth
              label="الوصف"
              variant="filled"
              multiline
              rows={3}
              value={newTicket.description}
              onChange={(e) => handleChange("description", e.target.value)}
              sx={{
                background: "#2d3134",
                input: { color: "#fff" },
                label: { color: "#6fd943" },
              }}
              error={!!formErrors.description}
              helperText={formErrors.description}
            />

            <FormControl fullWidth color="success">
              <InputLabel id="priority-label" sx={{ color: "#6fd943" }}>
                الأولوية
              </InputLabel>
              <Select
                labelId="priority-label"
                value={newTicket.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                sx={{ background: "#2d3134", color: "#fff" }}
              >
                <MenuItem value="Low">منخفضة</MenuItem>
                <MenuItem value="Normal">عادية</MenuItem>
                <MenuItem value="High">مرتفعة</MenuItem>
                <MenuItem value="Critical">حرجة</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth color="success">
              <InputLabel id="assigned-label" sx={{ color: "#6fd943" }}>
                تعيين إلى
              </InputLabel>
              <Select
                labelId="assigned-label"
                value={newTicket.assigned_to}
                onChange={(e) => handleAssignChange(e.target.value)}
                sx={{ background: "#2d3134", color: "#fff" }}
              >
                <MenuItem value="غير مخصص">غير مخصص</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.employee_id} value={emp.employee_id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth color="success">
              <InputLabel id="company-label" sx={{ color: "#6fd943" }}>
                الشركة
              </InputLabel>
              <Select
                labelId="company-label"
                value={newTicket.company}
                onChange={(e) => handleChange("company", e.target.value)}
                sx={{ background: "#2d3134", color: "#fff" }}
              >
                <MenuItem value="">اختر الشركة</MenuItem>
                {companies.map((c) =>
                  typeof c === "string" ? (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ) : (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name}
                    </MenuItem>
                  )
                )}
              </Select>
              {formErrors.company && (
                <small style={{ color: "red" }}>{formErrors.company}</small>
              )}
            </FormControl>

            <FormControl fullWidth color="success" sx={{ mt: 2 }}>
              <InputLabel id="department-label" sx={{ color: '#6fd943' }}>القسم</InputLabel>
              <Select
                labelId="department-label"
                value={department}
                label="القسم"
                onChange={(e) => setDepartment(e.target.value)}
                sx={{ background: '#2d3134', color: '#fff' }}
              >
                <MenuItem value=""><em>اختر القسم</em></MenuItem>
                {departments.map((dep) => (
                  <MenuItem key={dep.id} value={dep.name}>{dep.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Button
                variant="outlined"
                component="label"
                // startIcon={<DownloadIcon />}
                sx={{
                  color: "#6fd943",
                  borderColor: "#6fd943",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "rgba(111,217,67,0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(111,217,67,0.12)",
                    borderColor: "#8ffb6f",
                  },
                }}
              >
                اختر ملفًا
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={(e) => setAttachment(e.target.files[0])}
                />
              </Button>

              {attachment && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "#aaa", display: "block", fontSize: 13 }}
                >
                  الملف المختار: {attachment.name}
                </Typography>
              )}
            </Box>

            {addError && <Alert severity="error">{addError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Button onClick={onClose} sx={{ color: "#fff", bgcolor: "red" }}>
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={addLoading}
          >
            {addLoading ? "جاري الإضافة..." : "إضافة"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog النجاح */}
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            backgroundColor: "#2e7d32",
            color: "#fff",
            minWidth: 300,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
          ✅ تم الإضافة بنجاح
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>تمت إضافة التذكرة إلى النظام بنجاح.</Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSuccessDialogOpen(false)}
            sx={{ color: "#fff", border: "1px solid #fff", bgcolor: "red" }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddTicketDialog;
